// Static data for the PAM Integration Guide page.

export interface PayloadField {
  name: string;
  type: string;
  required: boolean;
  description: string;
  enum?: string[];
}

export interface EventSpec {
  name: string;
  domain: 'casino' | 'sport' | 'finance' | 'player';
  description: string;
  fields: PayloadField[];
  example: Record<string, unknown>;
}

export interface EnvelopeField {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

// ── Envelope ────────────────────────────────────────────────

export const ENVELOPE_FIELDS: EnvelopeField[] = [
  { name: 'event_id',       type: 'string (UUID)',    required: true,  description: 'Unique event identifier. Used as idempotency key — duplicates are deduplicated.' },
  { name: 'event_date',     type: 'string (ISO 8601)', required: true, description: 'When the event occurred (UTC). Must be within the last 48 hours.' },
  { name: 'site_id',        type: 'integer',          required: true,  description: 'Site/operator ID (>= 1). Must be authorized for the API key.' },
  { name: 'player_id',      type: 'string',           required: true,  description: 'Player identifier. Can be int-as-string or UUID.' },
  { name: 'event_type',     type: 'string',           required: true,  description: 'One of 34 event types. Determines payload validation schema.' },
  { name: 'domain',         type: 'string',           required: true,  description: 'casino, sport, finance, or player. Auto-inferred from event_type.' },
  { name: 'payload',        type: 'object',           required: true,  description: 'Event-specific data. Validated against JSON Schema.' },
  { name: 'produced_at',    type: 'string (ISO 8601)', required: true, description: 'When the PAM produced the event (UTC).' },
  { name: 'producer_app',   type: 'string',           required: true,  description: 'Identifier of the producing system (e.g. "my-pam").' },
  { name: 'schema_version', type: 'string',           required: true,  description: 'Schema version, format: v1, v2, etc.' },
];

// ── Auth ────────────────────────────────────────────────────

export interface AuthInfo {
  header: string;
  format: string;
  hash: string;
  cacheTTL: string;
}

export const AUTH_INFO: AuthInfo = {
  header: 'X-API-Key',
  format: 'sk_live_ + 40 hex chars',
  hash: 'Argon2id (server-side)',
  cacheTTL: '5 minutes',
};

export interface ErrorCode {
  status: number;
  code: string;
  description: string;
}

export const ERROR_CODES: ErrorCode[] = [
  { status: 400, code: 'unsupported_media_type', description: 'Content-Type is not application/json' },
  { status: 400, code: 'invalid_request',        description: 'Malformed JSON body' },
  { status: 400, code: 'validation_error',       description: 'Envelope or payload validation failed' },
  { status: 401, code: 'missing_api_key',        description: 'X-API-Key header not present' },
  { status: 401, code: 'invalid_api_key',        description: 'API key not found or inactive' },
  { status: 403, code: 'site_not_allowed',       description: 'site_id not authorized for this key' },
  { status: 500, code: 'internal_error',         description: 'Server-side processing error' },
];

// ── Events ──────────────────────────────────────────────────

export const EVENT_SPECS: EventSpec[] = [
  // ── Casino ──
  {
    name: 'casino_bet',
    domain: 'casino',
    description: 'A player places a casino bet (round start). One event per player action.',
    fields: [
      { name: 'round_id',       type: 'string',  required: true,  description: 'Game round identifier' },
      { name: 'transaction_id', type: 'integer',  required: true,  description: 'Unique transaction within round (>= 0)' },
      { name: 'brand',          type: 'string',  required: true,  description: 'Game brand slug (e.g. pragmatic, evolution)' },
      { name: 'game',           type: 'string',  required: true,  description: 'Game slug/identifier' },
      { name: 'game_type',      type: 'string',  required: false, description: 'Game category', enum: ['slots', 'table', 'live', 'crash'] },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Bet from real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Bet from bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'ISO 4217 or crypto ticker (e.g. EUR, USDT)' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after transaction' },
      { name: 'session_id',     type: 'string',  required: false, description: 'Game session identifier' },
    ],
    example: { round_id: 'r-abc-123', transaction_id: 10001, brand: 'pragmatic', game: 'sweet-bonanza', game_type: 'slots', amount_real: 8.50, amount_bonus: 1.50, currency: 'EUR' },
  },
  {
    name: 'casino_win',
    domain: 'casino',
    description: 'A player wins in a casino round.',
    fields: [
      { name: 'round_id',       type: 'string',  required: true,  description: 'Same round_id as the bet' },
      { name: 'transaction_id', type: 'integer',  required: true,  description: 'Unique transaction (>= 0)' },
      { name: 'brand',          type: 'string',  required: true,  description: 'Game brand' },
      { name: 'game',           type: 'string',  required: true,  description: 'Game slug' },
      { name: 'game_type',      type: 'string',  required: false, description: 'Game category', enum: ['slots', 'table', 'live', 'crash'] },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Win to real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Win to bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after win' },
    ],
    example: { round_id: 'r-abc-123', transaction_id: 10002, brand: 'pragmatic', game: 'sweet-bonanza', amount_real: 25.00, amount_bonus: 0.00, currency: 'EUR' },
  },
  {
    name: 'casino_refund',
    domain: 'casino',
    description: 'A casino bet is voided/refunded.',
    fields: [
      { name: 'round_id',       type: 'string',  required: true,  description: 'Same round_id as the bet' },
      { name: 'transaction_id', type: 'integer',  required: true,  description: 'Unique transaction (>= 0)' },
      { name: 'brand',          type: 'string',  required: true,  description: 'Game brand' },
      { name: 'game',           type: 'string',  required: true,  description: 'Game slug' },
      { name: 'game_type',      type: 'string',  required: false, description: 'Game category', enum: ['slots', 'table', 'live', 'crash'] },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Refund to real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Refund to bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after refund' },
      { name: 'reason',         type: 'string',  required: false, description: 'Refund reason' },
    ],
    example: { round_id: 'r-abc-123', transaction_id: 10003, brand: 'pragmatic', game: 'sweet-bonanza', amount_real: 8.50, amount_bonus: 1.50, currency: 'EUR', reason: 'game_malfunction' },
  },

  // ── Sport ──
  {
    name: 'sport_place_bet',
    domain: 'sport',
    description: 'A player places a sportsbook bet.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'bet_id',         type: 'string',  required: true,  description: 'Bet identifier' },
      { name: 'bet_type',       type: 'string',  required: false, description: 'Bet type', enum: ['single', 'parlay', 'system'] },
      { name: 'sport',          type: 'string',  required: false, description: 'Sport name' },
      { name: 'league',         type: 'string',  required: false, description: 'League/competition' },
      { name: 'event_id',       type: 'integer', required: false, description: 'Sporting event ID' },
      { name: 'market',         type: 'string',  required: false, description: 'Market type (e.g. match_winner)' },
      { name: 'odds',           type: 'number',  required: false, description: 'Odds value (> 0)' },
      { name: 'odds_format',    type: 'string',  required: false, description: 'Odds format', enum: ['decimal', 'fractional', 'american'] },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Stake from real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Stake from bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after bet' },
      { name: 'stake_tax_amount', type: 'number', required: false, description: 'Stake tax withheld (betting-tax jurisdictions)' },
    ],
    example: { movement_id: 50001, bet_id: 'bet-xyz-789', bet_type: 'single', sport: 'football', league: 'premier-league', odds: 2.10, odds_format: 'decimal', amount_real: 20.00, amount_bonus: 0.00, currency: 'EUR' },
  },
  {
    name: 'sport_win',
    domain: 'sport',
    description: 'A sportsbook bet wins.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'bet_id',         type: 'string',  required: true,  description: 'Bet identifier' },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Win to real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Win to bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after win' },
      { name: 'win_tax_amount', type: 'number', required: false, description: 'Win tax withheld (winning-tax jurisdictions)' },
    ],
    example: { movement_id: 50002, bet_id: 'bet-xyz-789', amount_real: 42.00, amount_bonus: 0.00, currency: 'EUR' },
  },
  {
    name: 'sport_cancel_bet',
    domain: 'sport',
    description: 'A sportsbook bet is cancelled.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'bet_id',         type: 'string',  required: true,  description: 'Bet identifier' },
      { name: 'cancel_reason',  type: 'string',  required: true,  description: 'Cancellation reason', enum: ['event_cancelled', 'odds_change', 'manual', 'system_error'] },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Return to real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Return to bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after cancellation' },
    ],
    example: { movement_id: 50003, bet_id: 'bet-xyz-789', cancel_reason: 'event_cancelled', amount_real: 20.00, amount_bonus: 0.00, currency: 'EUR' },
  },
  {
    name: 'sport_cashout_bet',
    domain: 'sport',
    description: 'Early cashout of a sportsbook bet.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'bet_id',         type: 'string',  required: true,  description: 'Bet identifier' },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Cashout to real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Cashout to bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after cashout' },
    ],
    example: { movement_id: 50004, bet_id: 'bet-xyz-789', amount_real: 15.00, amount_bonus: 0.00, currency: 'EUR' },
  },
  {
    name: 'sport_win_rollback',
    domain: 'sport',
    description: 'Reversal of a sport win.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'bet_id',         type: 'string',  required: true,  description: 'Bet identifier' },
      { name: 'amount_real',    type: 'number',  required: true,  description: 'Rollback from real balance (>= 0)' },
      { name: 'amount_bonus',   type: 'number',  required: true,  description: 'Rollback from bonus balance (>= 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after rollback' },
    ],
    example: { movement_id: 50005, bet_id: 'bet-xyz-789', amount_real: 42.00, amount_bonus: 0.00, currency: 'EUR' },
  },

  // ── Finance ──
  {
    name: 'deposit_completed',
    domain: 'finance',
    description: 'A successful deposit (money arrived in player wallet).',
    fields: [
      { name: 'movement_id',            type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'payment_method',         type: 'string',  required: true,  description: 'visa, mastercard, pix, crypto, etc.' },
      { name: 'external_system',        type: 'string',  required: true,  description: 'Payment processor (checkout-com, stripe, etc.)' },
      { name: 'external_transaction_id', type: 'string', required: true,  description: 'External transaction reference' },
      { name: 'amount',                 type: 'number',  required: true,  description: 'Deposit amount (> 0)' },
      { name: 'currency',               type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance',         type: 'number',  required: false, description: 'Balance after deposit' },
      { name: 'is_first_deposit',       type: 'boolean', required: false, description: 'If true, counts toward FTD metrics' },
      { name: 'bonus_code',             type: 'string',  required: false, description: 'Bonus/promo code used' },
    ],
    example: { movement_id: 70001, payment_method: 'visa', external_system: 'checkout-com', external_transaction_id: 'ch_abc123', amount: 100.00, currency: 'EUR', is_first_deposit: true },
  },
  {
    name: 'withdrawal_paid',
    domain: 'finance',
    description: 'A completed withdrawal.',
    fields: [
      { name: 'movement_id',            type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'payment_method',         type: 'string',  required: true,  description: 'Payment method' },
      { name: 'external_system',        type: 'string',  required: true,  description: 'Payment processor' },
      { name: 'external_transaction_id', type: 'string', required: true,  description: 'External reference' },
      { name: 'amount',                 type: 'number',  required: true,  description: 'Withdrawal amount (> 0)' },
      { name: 'currency',               type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance',         type: 'number',  required: false, description: 'Balance after withdrawal' },
    ],
    example: { movement_id: 70002, payment_method: 'bank_transfer', external_system: 'checkout-com', external_transaction_id: 'ch_wd_456', amount: 200.00, currency: 'EUR' },
  },
  {
    name: 'withdrawal_cancelled',
    domain: 'finance',
    description: 'A cancelled withdrawal.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'payment_method', type: 'string',  required: true,  description: 'Payment method' },
      { name: 'external_system', type: 'string', required: true,  description: 'Payment processor' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Withdrawal amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after cancellation' },
      { name: 'reason',         type: 'string',  required: false, description: 'Cancellation reason', enum: ['player_request', 'compliance', 'operator_decision'] },
    ],
    example: { movement_id: 70003, payment_method: 'bank_transfer', external_system: 'checkout-com', amount: 200.00, currency: 'EUR', reason: 'player_request' },
  },
  {
    name: 'withdrawal_rejected',
    domain: 'finance',
    description: 'A withdrawal rejected during review (operator/system decision, money returned to the wallet). Distinct from withdrawal_cancelled, which is typically player-initiated.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'payment_method', type: 'string',  required: true,  description: 'Payment method' },
      { name: 'external_system', type: 'string', required: true,  description: 'Payment processor' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Withdrawal amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after rejection' },
      { name: 'reason',         type: 'string',  required: false, description: 'Rejection reason', enum: ['operator_decision', 'compliance', 'kyc_missing', 'external_system'] },
    ],
    example: { movement_id: 70004, payment_method: 'bank_transfer', external_system: 'checkout-com', amount: 350.00, currency: 'EUR', reason: 'kyc_missing' },
  },
  {
    name: 'deposit_requested',
    domain: 'finance',
    description: 'A deposit attempt initiated (PSP operation created). First step of the deposit conversion funnel.',
    fields: [
      { name: 'operation_id',   type: 'integer', required: true,  description: 'PSP operation ID (>= 0)' },
      { name: 'payment_method', type: 'string',  required: true,  description: 'Payment method' },
      { name: 'external_system', type: 'string', required: true,  description: 'Payment processor' },
      { name: 'external_transaction_id', type: 'string', required: false, description: 'External reference' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Requested amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
    ],
    example: { operation_id: 80001, payment_method: 'visa', external_system: 'checkout-com', amount: 100.00, currency: 'EUR' },
  },
  {
    name: 'deposit_failed',
    domain: 'finance',
    description: 'A deposit attempt that did not complete (rejected/declined by the PSP or expired).',
    fields: [
      { name: 'operation_id',   type: 'integer', required: true,  description: 'PSP operation ID (>= 0)' },
      { name: 'payment_method', type: 'string',  required: true,  description: 'Payment method' },
      { name: 'external_system', type: 'string', required: true,  description: 'Payment processor' },
      { name: 'external_transaction_id', type: 'string', required: false, description: 'External reference' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Failed amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'reason',         type: 'string',  required: true,  description: 'Failure reason', enum: ['rejected', 'declined', 'expired'] },
    ],
    example: { operation_id: 80002, payment_method: 'visa', external_system: 'checkout-com', amount: 100.00, currency: 'EUR', reason: 'declined' },
  },
  {
    name: 'withdrawal_requested',
    domain: 'finance',
    description: 'A withdrawal requested by the player (funds reserved). Mirrors the pull-channel v1.2 event stream.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'payment_method', type: 'string',  required: true,  description: 'Payout method' },
      { name: 'external_system', type: 'string', required: false, description: 'Payment processor' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Requested amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after the reservation' },
    ],
    example: { movement_id: 70005, payment_method: 'bank_transfer', amount: 200.00, currency: 'EUR' },
  },
  {
    name: 'withdrawal_approved',
    domain: 'finance',
    description: 'A withdrawal approved by the operator (ready for payment processing).',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Approved amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'payment_method', type: 'string',  required: false, description: 'Payout method' },
      { name: 'author_id',      type: 'integer', required: false, description: 'Approving operator network ID' },
    ],
    example: { movement_id: 70005, amount: 200.00, currency: 'EUR', author_id: 12 },
  },
  {
    name: 'bonus_emitted',
    domain: 'finance',
    description: 'A bonus is issued to a player.',
    fields: [
      { name: 'bonus_id',             type: 'integer', required: true,  description: 'Bonus identifier (>= 0)' },
      { name: 'bonus_name',           type: 'string',  required: true,  description: 'Bonus display name' },
      { name: 'bonus_type',           type: 'string',  required: true,  description: 'Bonus type', enum: ['casino_real_money', 'crash_game_real_money', 'sportsbook_free_bet', 'deposit_match', 'free_spins'] },
      { name: 'amount',               type: 'number',  required: true,  description: 'Bonus amount (> 0)' },
      { name: 'currency',             type: 'string',  required: true,  description: 'Currency code' },
      { name: 'promo_id',             type: 'integer', required: false, description: 'Promotion campaign ID' },
      { name: 'wagering_requirement', type: 'number',  required: false, description: 'Wagering multiplier (e.g. 35.0 = 35x)' },
    ],
    example: { bonus_id: 90001, bonus_name: 'Welcome Bonus', bonus_type: 'deposit_match', amount: 50.00, currency: 'EUR', wagering_requirement: 35.0 },
  },
  {
    name: 'bonus_redeemed',
    domain: 'finance',
    description: 'A bonus is redeemed (converted or consumed).',
    fields: [
      { name: 'bonus_id',       type: 'integer', required: true,  description: 'Bonus identifier (>= 0)' },
      { name: 'bonus_name',     type: 'string',  required: true,  description: 'Bonus display name' },
      { name: 'amount',         type: 'number',  required: true,  description: 'Redeemed amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after redemption' },
    ],
    example: { bonus_id: 90001, bonus_name: 'Welcome Bonus', amount: 50.00, currency: 'EUR' },
  },
  {
    name: 'bonus_activated',
    domain: 'finance',
    description: 'A granted bonus became active/wagering (the player started using it).',
    fields: [
      { name: 'bonus_id',       type: 'integer', required: true,  description: 'Bonus identifier (>= 0)' },
      { name: 'bonus_type',     type: 'string',  required: true,  description: 'Bonus type', enum: ['casino_real_money', 'crash_game_real_money', 'sportsbook_free_bet', 'deposit_match', 'free_spins'] },
      { name: 'amount',         type: 'number',  required: true,  description: 'Bonus amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'wagered_amount', type: 'number',  required: false, description: 'Amount wagered against the bonus so far' },
    ],
    example: { bonus_id: 90001, bonus_type: 'deposit_match', amount: 50.00, currency: 'EUR', wagered_amount: 12.50 },
  },
  {
    name: 'bonus_expired',
    domain: 'finance',
    description: 'A bonus expired before being fully wagered/redeemed.',
    fields: [
      { name: 'bonus_id',        type: 'integer', required: true,  description: 'Bonus identifier (>= 0)' },
      { name: 'bonus_type',      type: 'string',  required: true,  description: 'Bonus type', enum: ['casino_real_money', 'crash_game_real_money', 'sportsbook_free_bet', 'deposit_match', 'free_spins'] },
      { name: 'amount',          type: 'number',  required: true,  description: 'Original bonus amount (> 0)' },
      { name: 'currency',        type: 'string',  required: true,  description: 'Currency code' },
      { name: 'residual_amount', type: 'number',  required: false, description: 'Unused bonus balance at expiry' },
    ],
    example: { bonus_id: 90002, bonus_type: 'free_spins', amount: 20.00, currency: 'EUR', residual_amount: 6.00 },
  },
  {
    name: 'bonus_cancelled',
    domain: 'finance',
    description: 'A bonus cancelled/suspended/forfeited by the operator or the system.',
    fields: [
      { name: 'bonus_id',   type: 'integer', required: true,  description: 'Bonus identifier (>= 0)' },
      { name: 'bonus_type', type: 'string',  required: true,  description: 'Bonus type', enum: ['casino_real_money', 'crash_game_real_money', 'sportsbook_free_bet', 'deposit_match', 'free_spins'] },
      { name: 'amount',     type: 'number',  required: true,  description: 'Original bonus amount (> 0)' },
      { name: 'currency',   type: 'string',  required: true,  description: 'Currency code' },
      { name: 'reason',     type: 'string',  required: false, description: 'Cancellation reason', enum: ['suspended', 'forfait', 'deleted'] },
    ],
    example: { bonus_id: 90003, bonus_type: 'deposit_match', amount: 50.00, currency: 'EUR', reason: 'forfait' },
  },
  {
    name: 'cashback_completed',
    domain: 'finance',
    description: 'A cashback payout to a player.',
    fields: [
      { name: 'movement_id', type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'product',     type: 'string',  required: true,  description: 'Product', enum: ['casino', 'casino_live', 'crash', 'sportsbook'] },
      { name: 'amount',      type: 'number',  required: true,  description: 'Cashback amount (> 0)' },
      { name: 'currency',    type: 'string',  required: true,  description: 'Currency code' },
    ],
    example: { movement_id: 70004, product: 'casino', amount: 12.50, currency: 'EUR' },
  },
  {
    name: 'manual_adjustment',
    domain: 'finance',
    description: 'A manual wallet adjustment performed by an operator (manual deposit/withdrawal, cash store). Bronze-only: whether it counts into NetCash is a pending product decision.',
    fields: [
      { name: 'movement_id',    type: 'integer', required: true,  description: 'Financial movement ID (>= 0)' },
      { name: 'direction',      type: 'string',  required: true,  description: 'Adjustment direction', enum: ['credit', 'debit'] },
      { name: 'amount',         type: 'number',  required: true,  description: 'Adjustment amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'reason',         type: 'string',  required: true,  description: 'Adjustment causal', enum: ['manual_deposit', 'manual_withdrawal', 'cash_store'] },
      { name: 'note',           type: 'string',  required: false, description: 'Free-text operator note' },
      { name: 'author_id',      type: 'integer', required: false, description: 'Operator network ID' },
      { name: 'player_balance', type: 'number',  required: false, description: 'Balance after the adjustment' },
    ],
    example: { movement_id: 70010, direction: 'credit', amount: 25.00, currency: 'EUR', reason: 'manual_deposit', author_id: 12 },
  },
  {
    name: 'network_transfer_completed',
    domain: 'finance',
    description: 'A completed balance transfer between network accounts (agent/shop and player).',
    fields: [
      { name: 'transfer_id',    type: 'integer', required: true,  description: 'Network transfer ID (>= 0)' },
      { name: 'transfer_type',  type: 'string',  required: true,  description: 'Transfer type', enum: ['direct_deposit', 'direct_withdrawal', 'bank_deposit', 'bank_withdrawal'] },
      { name: 'amount',         type: 'number',  required: true,  description: 'Transferred amount (> 0)' },
      { name: 'currency',       type: 'string',  required: true,  description: 'Currency code' },
      { name: 'counterpart_id', type: 'string',  required: true,  description: 'Network ID of the other party' },
      { name: 'code',           type: 'string',  required: false, description: 'Transfer code' },
    ],
    example: { transfer_id: 5001, transfer_type: 'direct_deposit', amount: 150.00, currency: 'EUR', counterpart_id: '100234' },
  },

  // ── Player ──
  {
    name: 'create_player',
    domain: 'player',
    description: 'New player registration. Send this before any other event for a player.',
    fields: [
      { name: 'username',      type: 'string', required: true,  description: 'Player username' },
      { name: 'status',        type: 'string', required: true,  description: 'Account status', enum: ['ENABLED', 'DISABLED', 'PENDING', 'VALIDATED', 'DELETED', 'SELF_EXCLUDED', 'FROZEN'] },
      { name: 'country_code',  type: 'string', required: true,  description: 'ISO 3166-1 alpha-2 (e.g. IT, BR)' },
      { name: 'language_code', type: 'string', required: true,  description: 'ISO 639-1 (e.g. it, en)' },
      { name: 'currency_code', type: 'string', required: true,  description: "Player's base currency" },
      { name: 'email',         type: 'string', required: false, description: 'Email address' },
      { name: 'phone',         type: 'string', required: false, description: 'Phone number' },
      { name: 'first_name',    type: 'string', required: false, description: 'First name' },
      { name: 'last_name',     type: 'string', required: false, description: 'Last name' },
      { name: 'birth_date',    type: 'string', required: false, description: 'Date of birth (YYYY-MM-DD)' },
      { name: 'affiliate_id',  type: 'string', required: false, description: 'Affiliate/referrer ID' },
      { name: 'gender',        type: 'string', required: false, description: 'Gender', enum: ['M', 'F', 'N/A'] },
      { name: 'ip_address',    type: 'string', required: false, description: 'Registration IP' },
      { name: 'user_agent',    type: 'string', required: false, description: 'Browser user agent' },
      { name: 'device_type',   type: 'string', required: false, description: 'Device type', enum: ['desktop', 'mobile'] },
      { name: 'referer',       type: 'string', required: false, description: 'Referrer URL' },
    ],
    example: { username: 'john_doe', status: 'ENABLED', country_code: 'IT', language_code: 'it', currency_code: 'EUR', email: 'john@example.com' },
  },
  {
    name: 'player_login',
    domain: 'player',
    description: 'Player login event. All payload fields are optional.',
    fields: [
      { name: 'ip_address',   type: 'string',  required: false, description: 'Login IP address' },
      { name: 'user_agent',   type: 'string',  required: false, description: 'Browser user agent' },
      { name: 'device_type',  type: 'string',  required: false, description: 'Device type', enum: ['desktop', 'mobile'] },
      { name: 'country_code', type: 'string',  required: false, description: 'Country (ISO 3166-1)' },
      { name: 'session_id',   type: 'string',  required: false, description: 'Session identifier' },
      { name: 'success',      type: 'boolean', required: false, description: 'Login success flag' },
    ],
    example: { ip_address: '203.0.113.42', device_type: 'mobile', country_code: 'IT', success: true },
  },
  {
    name: 'player_status_changed',
    domain: 'player',
    description: 'Player account status change.',
    fields: [
      { name: 'old_status', type: 'string', required: true,  description: 'Previous status', enum: ['ENABLED', 'DISABLED', 'PENDING', 'VALIDATED', 'DELETED', 'SELF_EXCLUDED', 'FROZEN'] },
      { name: 'new_status', type: 'string', required: true,  description: 'New status', enum: ['ENABLED', 'DISABLED', 'PENDING', 'VALIDATED', 'DELETED', 'SELF_EXCLUDED', 'FROZEN'] },
      { name: 'author',     type: 'object', required: false, description: 'Who made the change: { id: number, username: string }' },
    ],
    example: { old_status: 'PENDING', new_status: 'ENABLED', author: { id: 1, username: 'admin' } },
  },
  {
    name: 'player_email_confirmed',
    domain: 'player',
    description: 'Email verification event.',
    fields: [
      { name: 'email', type: 'string', required: false, description: 'Confirmed email address' },
    ],
    example: { email: 'john@example.com' },
  },
  {
    name: 'player_kyc_verified',
    domain: 'player',
    description: 'KYC verification event.',
    fields: [
      { name: 'is_verified', type: 'boolean', required: false, description: 'Verification result' },
      { name: 'author',      type: 'object',  required: false, description: 'Who verified: { id: number, username: string }' },
    ],
    example: { is_verified: true, author: { id: 1, username: 'compliance_team' } },
  },
  {
    name: 'player_logout',
    domain: 'player',
    description: 'A player session closed; event_date is the closing time. Complements player_login for session-duration analytics.',
    fields: [
      { name: 'session_id',           type: 'string',  required: true,  description: 'Same value sent on the matching player_login' },
      { name: 'session_duration_sec', type: 'integer', required: false, description: 'Session duration in seconds' },
      { name: 'ip_address',           type: 'string',  required: false, description: 'Client IP address at login' },
    ],
    example: { session_id: 'sess-xyz', session_duration_sec: 1860 },
  },
  {
    name: 'player_phone_confirmed',
    domain: 'player',
    description: 'The player confirmed their mobile phone number (mirror of player_email_confirmed).',
    fields: [
      { name: 'phone',  type: 'string', required: true,  description: 'Confirmed phone number' },
      { name: 'prefix', type: 'string', required: false, description: 'International prefix' },
    ],
    example: { phone: '333123456', prefix: '+39' },
  },
  {
    name: 'self_exclusion_created',
    domain: 'player',
    description: 'A self-exclusion period started for the player (responsible gaming).',
    fields: [
      { name: 'exclusion_type', type: 'string',  required: true,  description: 'Exclusion type', enum: ['temporary', 'permanent'] },
      { name: 'end_date',       type: 'string',  required: true,  description: 'ISO 8601 — when the exclusion ends' },
      { name: 'channel',        type: 'string',  required: false, description: 'Where it was requested', enum: ['online', 'backoffice'] },
      { name: 'author_id',      type: 'integer', required: false, description: 'Backoffice operator (backoffice channel only)' },
    ],
    example: { exclusion_type: 'temporary', end_date: '2026-10-18T00:00:00Z', channel: 'online' },
  },
  {
    name: 'self_exclusion_deleted',
    domain: 'player',
    description: 'A self-exclusion removed before its natural end (backoffice decision).',
    fields: [
      { name: 'exclusion_type', type: 'string',  required: true,  description: 'Exclusion type', enum: ['temporary', 'permanent'] },
      { name: 'reason',         type: 'string',  required: false, description: 'Revocation reason' },
      { name: 'author_id',      type: 'integer', required: false, description: 'Backoffice operator' },
    ],
    example: { exclusion_type: 'temporary', reason: 'backoffice_review', author_id: 12 },
  },
  {
    name: 'document_status_changed',
    domain: 'player',
    description: 'A KYC document attachment changed status during the verification flow. Complements player_kyc_verified (the final account-level verdict).',
    fields: [
      { name: 'document_type',   type: 'string',  required: true,  description: 'Country-specific catalog (DNI, CPF, Passport, ...)' },
      { name: 'attachment_type', type: 'string',  required: true,  description: 'Attachment side/type', enum: ['front', 'back', 'selfie'] },
      { name: 'status',          type: 'string',  required: true,  description: 'New attachment status', enum: ['pending_upload', 'pending_verification', 'approved', 'rejected'] },
      { name: 'document_id',     type: 'integer', required: false, description: 'Document identifier' },
      { name: 'reason',          type: 'string',  required: false, description: 'Review note (rejections)' },
      { name: 'author_id',       type: 'integer', required: false, description: 'Reviewing operator' },
    ],
    example: { document_type: 'CPF', attachment_type: 'front', status: 'approved', author_id: 12 },
  },
];

// ── Best Practices ──────────────────────────────────────────

export interface BestPractice {
  title: string;
  items: string[];
}

export const BEST_PRACTICES: BestPractice[] = [
  {
    title: 'Idempotency',
    items: [
      'Use a deterministic event_id (UUID v5 based on transaction data) so retries are safe.',
      'The system deduplicates by event_id — resending is harmless.',
    ],
  },
  {
    title: 'Event Ordering',
    items: [
      'Send create_player before any other event for a new player.',
      'Casino: send casino_bet before casino_win or casino_refund.',
      'Sport: send sport_place_bet before settlement events.',
      'Finance: send deposit_completed when payment is confirmed, not requested.',
    ],
  },
  {
    title: 'Currency',
    items: [
      'Use the same currency value in the envelope and payload.',
      'Uppercase ISO 4217 codes: EUR, USD, BRL, GBP.',
      'Crypto: standard tickers: USDT, BTC, ETH, DOGE.',
    ],
  },
  {
    title: 'Real vs Bonus Amounts',
    items: [
      'Casino/sport events split amounts into amount_real and amount_bonus.',
      'Both fields are always required, even when one is 0.00.',
      'Example: fully real bet = { amount_real: 10, amount_bonus: 0 }.',
    ],
  },
  {
    title: 'Retry Strategy',
    items: [
      '4xx errors: do not retry — fix the payload.',
      '5xx errors: retry with exponential backoff (max 3 retries).',
      'Network timeouts: retry once after 100ms.',
      'Recommended timeout: 10 seconds per request.',
    ],
  },
  {
    title: 'Throughput',
    items: [
      'HTTP: one event per request. Use connection pooling for high volume.',
      'gRPC IngestStream: preferred for batch imports and migrations.',
      'Max request body size: 1 MB.',
      'event_date must be within the last 48 hours.',
    ],
  },
];
